import { useEffect } from 'react';
import { presenceService } from '@/services/api/presenceService';

const HEARTBEAT_MS = 60_000;

/** Ping the API while this tab is visible so the user shows as online. */
export function usePresenceHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;

    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      void presenceService.heartbeat().catch(() => undefined);
    };

    const start = () => {
      if (timer != null) return;
      beat();
      timer = window.setInterval(beat, HEARTBEAT_MS);
    };

    const stop = () => {
      if (timer == null) return;
      window.clearInterval(timer);
      timer = undefined;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    const onPageHide = () => {
      stop();
      presenceService.markOfflineOnUnload();
    };

    const onPageShow = () => {
      if (document.visibilityState === 'visible') start();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled]);
}
